import React, { useState } from "react";
import { Mail, MapPin, Sparkles, RefreshCw, Send, CheckCircle2 } from "lucide-react";

const CONTACT_EMAIL = "sheetal@aroveniq.in";
const CALENDLY_URL = "https://calendly.com/aroveniq/procurement-audit";

export default function Contact() {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [message, setMessage] = useState("");

  const [loading, setLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleFormSubmit = (e) => {
    e.preventDefault();
    setLoading(true);

    // Static site, no backend: hand the lead off to the visitor's own email
    // client, addressed to us, instead of posting to an API that doesn't exist.
    const subject = `Procurement Audit request — ${company}`;
    const body = [
      `Name: ${fullName}`,
      `Email: ${email}`,
      `Company: ${company}`,
      "",
      "Needs:",
      message
    ].join("\n");

    window.location.href = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

    setIsSuccess(true);
    setLoading(false);
  };

  return (
    <div className="bg-[#05050A] text-white min-h-screen py-24 relative overflow-hidden" data-testid="contact-page">
      
      {/* Background glow visual blobs */}
      <div className="absolute top-20 right-1/4 w-[400px] h-[400px] bg-[#7C3AED]/5 rounded-full blur-[130px] pointer-events-none" />
      <div className="absolute bottom-20 left-1/4 w-[500px] h-[500px] bg-[#EA7C3D]/4 rounded-full blur-[150px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Header Block */}
        <div className="text-center space-y-4 mb-16">
          <div className="inline-flex items-center space-x-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs font-semibold text-[#EA7C3D] tracking-wide">
            <Sparkles className="w-3.5 h-3.5 text-[#7C3AED]" />
            <span>Interactive Demo Booking & Lead Intake</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-black font-display tracking-tighter leading-none">Schedule your Procurement Audit</h1>
          <p className="text-[#ABA3C2] text-sm sm:text-base max-w-xl mx-auto">
            Book an absolute operational review session with our strategic sourcing consultants.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          
          {/* Left Column: Direct Contacts */}
          <div className="lg:col-span-5 space-y-8">
            <div className="p-8 rounded-3xl bg-white/5 border border-white/10 backdrop-blur-xl space-y-6">
              <h3 className="text-xl font-bold font-display text-white">Direct Sourcing Helpline</h3>
              <p className="text-xs text-zinc-400">
                Are you facing an active vendor SLA compliance dispute or disruption? Email our priority helpline for immediate assistance.
              </p>

              <div className="space-y-4 pt-4 border-t border-white/5">
                <div className="flex items-center space-x-3 text-sm text-[#ABA3C2]">
                  <Mail className="w-5 h-5 text-[#EA7C3D]" />
                  <span>{CONTACT_EMAIL}</span>
                </div>
                <div className="flex items-center space-x-3 text-sm text-[#ABA3C2]">
                  <MapPin className="w-5 h-5 text-[#EA7C3D]" />
                  <span>Pune, Maharashtra, India</span>
                </div>
              </div>
            </div>

            {/* Calendly Booking Embed Placeholder */}
            <div className="p-6 rounded-2xl bg-black/40 border border-white/5 space-y-3">
              <div className="text-xs font-mono font-bold text-[#EA7C3D] uppercase">Instant Calendar Booking</div>
              <p className="text-xs text-zinc-500">
                You can bypass our intake form completely and schedule a session directly on our calendar.
              </p>
              <a
                href={CALENDLY_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="calendly-link"
                className="block p-4 rounded-xl bg-white/5 border border-white/5 text-center text-xs text-zinc-400 font-semibold cursor-pointer hover:bg-white/10 transition-colors"
              >
                [ Click here to load Calendly Scheduler ]
              </a>
            </div>
          </div>

          {/* Right Column: Lead Form */}
          <div className="lg:col-span-7">
            <div className="p-8 rounded-3xl bg-[#14101F]/80 border border-white/10 backdrop-blur-xl" data-testid="contact-form-container">
              {!isSuccess ? (
                <form onSubmit={handleFormSubmit} className="space-y-6" data-testid="contact-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="space-y-1">
                      <label className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">Full Name</label>
                      <input
                        type="text"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        required
                        data-testid="contact-name-input"
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-[#7C3AED] transition-colors"
                        placeholder="Vikram Nair"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">Work Email</label>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        data-testid="contact-email-input"
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-[#7C3AED] transition-colors"
                        placeholder="vikram@apex.com"
                      />
                    </div>
                  </div>

                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">Company / Organization</label>
                    <input
                      type="text"
                      value={company}
                      onChange={(e) => setCompany(e.target.value)}
                      required
                      data-testid="contact-company-input"
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-[#7C3AED] transition-colors"
                      placeholder="Apex Industrials"
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">Your Message / Needs</label>
                    <textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      rows={4}
                      required
                      data-testid="contact-message-input"
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-[#7C3AED] transition-colors resize-none"
                      placeholder="Specify compliance tracking, vendor risk or spend overlap details."
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="contact-submit"
                    className="w-full py-3.5 rounded-full bg-gradient-to-r from-[#7C3AED] to-[#EA7C3D] hover:shadow-[0_0_20px_rgba(124,58,237,0.4)] text-white text-sm font-semibold tracking-wide transition-all flex items-center justify-center space-x-2"
                  >
                    {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    <span>{loading ? "Opening Email..." : "Submit Book a Demo Request"}</span>
                  </button>
                </form>
              ) : (
                <div className="text-center py-12 space-y-6 animate-fade-in" data-testid="contact-success-panel">
                  <div className="w-16 h-16 rounded-full bg-green-500/20 text-green-400 flex items-center justify-center mx-auto text-2xl font-bold border border-green-500/30">
                    <CheckCircle2 className="w-8 h-8" />
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-2xl font-black font-display text-white">Almost there!</h3>
                    <p className="text-sm text-zinc-400 max-w-md mx-auto">
                      Thank you, <strong className="text-white">{fullName}</strong>. We've opened an email to <strong className="text-white">{CONTACT_EMAIL}</strong> with your details for <strong className="text-white">{company}</strong> — just hit send from your mail app to complete your request.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

        </div>

      </div>
    </div>
  );
}